import styled from '@emotion/styled';
import type { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';

import type { BoxProps } from '../box/Box';
import { getBoxStyles } from '../box/Box';
import { themeVars } from '../theme/theme';

export type ActionIconProps = BoxProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ActionIconVariant;
    size?: ActionIconSize;
  };

export const actionIconVariantList = [
  'white',
  'neutral',
  'warm',
  'titan',
  'neon',
  'info',
  'notice',
  'error',
  'success',
  'magenta',
  'blue',
  'orange',
  'teal',
  'pink',
  'purple',
  'cyan',
  'violet',
  'celery',
  'yellow',
  'burgundy',
] as const;

export type ActionIconVariant = (typeof actionIconVariantList)[number];

export type ActionIconSize = 'sm' | 'xs';

const Component = forwardRef<HTMLButtonElement, PropsWithChildren<ActionIconProps>>(
  ({ children, variant = 'neutral', size = 'sm', disabled, ...props }, ref) => {
    return (
      <Root
        as={'button'}
        ref={ref}
        data-variant={variant}
        data-disabled={disabled}
        data-size={size}
        {...props}
      >
        <Hover data-hover />
        <Label>{children}</Label>
      </Root>
    );
  },
);

const Label = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const Hover = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  background-color: black;
  transition: all 0.3s ease;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 999px;
`;

const Root = styled.button<BoxProps>`
  ${getBoxStyles};

  overflow: hidden;
  position: relative;
  border: none;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 99px;
  user-select: none;
  outline: none;
  transition: all 0.2s ease;

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${themeVars.colors.white},
      0 0 0 4px ${themeVars.colors.info600};
  }

  &[data-size='sm'] {
    height: 32px;
    width: 32px;
    font-size: 20px;
  }

  &[data-size='xs'] {
    height: 24px;
    width: 24px;
    font-size: 16px;
  }

  &:not([data-disabled='true']):hover {
    [data-hover] {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }

  &[data-variant='warm'] {
    background: ${themeVars.colors.warmGrey250};
    color: ${themeVars.colors.warmGrey600};

    [data-hover] {
      background: ${themeVars.colors.warmGrey300};
    }
  }

  &[data-variant='white'] {
    color: ${themeVars.colors.coldGrey600};
    background: ${themeVars.colors.white};

    [data-hover] {
      background: ${themeVars.colors.coldGrey100};
    }
  }

  &[data-variant='neutral'] {
    background: ${themeVars.colors.coldGrey250};
    color: ${themeVars.colors.coldGrey700};

    [data-hover] {
      background: ${themeVars.colors.coldGrey100};
    }
  }

  &[data-variant='titan'] {
    background: ${themeVars.colors.titan250};
    color: ${themeVars.colors.titan700};

    [data-hover] {
      background: ${themeVars.colors.titan200};
    }
  }

  &[data-variant='neon'] {
    background: ${themeVars.colors.neonFrontRow200};
    color: ${themeVars.colors.neonFrontRow700};

    [data-hover] {
      background: ${themeVars.colors.neonFrontRow100};
    }
  }

  &[data-variant='info'] {
    background: ${themeVars.colors.info200};
    color: ${themeVars.colors.info700};

    [data-hover] {
      background: ${themeVars.colors.info100};
    }
  }

  &[data-variant='notice'] {
    background: ${themeVars.colors.notice200};
    color: ${themeVars.colors.notice700};

    [data-hover] {
      background: ${themeVars.colors.notice100};
    }
  }

  &[data-variant='error'] {
    background: ${themeVars.colors.error200};
    color: ${themeVars.colors.error700};

    [data-hover] {
      background: ${themeVars.colors.error100};
    }
  }

  &[data-variant='success'] {
    background: ${themeVars.colors.success200};
    color: ${themeVars.colors.success700};

    [data-hover] {
      background: ${themeVars.colors.success100};
    }
  }

  &[data-variant='magenta'] {
    background: ${themeVars.colors.magenta200};
    color: ${themeVars.colors.magenta700};

    [data-hover] {
      background: ${themeVars.colors.magenta100};
    }
  }

  &[data-variant='blue'] {
    background: ${themeVars.colors.blue200};
    color: ${themeVars.colors.blue700};

    [data-hover] {
      background: ${themeVars.colors.blue100};
    }
  }

  &[data-variant='orange'] {
    background: ${themeVars.colors.orange200};
    color: ${themeVars.colors.orange700};

    [data-hover] {
      background: ${themeVars.colors.orange100};
    }
  }

  &[data-variant='teal'] {
    background: ${themeVars.colors.teal200};
    color: ${themeVars.colors.teal700};

    [data-hover] {
      background: ${themeVars.colors.teal100};
    }
  }

  &[data-variant='pink'] {
    background: ${themeVars.colors.pink200};
    color: ${themeVars.colors.pink700};

    [data-hover] {
      background: ${themeVars.colors.pink100};
    }
  }

  &[data-variant='purple'] {
    background: ${themeVars.colors.purple200};
    color: ${themeVars.colors.purple700};

    [data-hover] {
      background: ${themeVars.colors.purple100};
    }
  }

  &[data-variant='cyan'] {
    background: ${themeVars.colors.cyan200};
    color: ${themeVars.colors.cyan700};

    [data-hover] {
      background: ${themeVars.colors.cyan100};
    }
  }

  &[data-variant='violet'] {
    background: ${themeVars.colors.violet200};
    color: ${themeVars.colors.violet700};

    [data-hover] {
      background: ${themeVars.colors.violet100};
    }
  }

  &[data-variant='celery'] {
    background: ${themeVars.colors.celery200};
    color: ${themeVars.colors.celery700};

    [data-hover] {
      background: ${themeVars.colors.celery100};
    }
  }

  &[data-variant='yellow'] {
    background: ${themeVars.colors.yellow200};
    color: ${themeVars.colors.yellow700};

    [data-hover] {
      background: ${themeVars.colors.yellow100};
    }
  }

  &[data-variant='burgundy'] {
    background: ${themeVars.colors.burgundy200};
    color: ${themeVars.colors.burgundy700};

    [data-hover] {
      background: ${themeVars.colors.burgundy100};
    }
  }

  &[data-disabled='true'] {
    cursor: not-allowed;
    background: transparent;
    color: ${themeVars.colors.coldGrey900}32;
  }

  &:not([data-disabled='true']):active {
    transform: translateY(3px);
  }
`;

export const ActionIcon = Object.assign(Component, {
  Root,
  Hover,
  Label,
});
